import { DOMAIN, useCanManageApplications, useCanViewCommitteesBool } from "@/hooks/useCapabilities";
import { useFlags, type FlagRecord } from "@/hooks/useFeatureFlags";
import type { NavLink } from "@/layouts/AppLayout/types";
import type { Path } from "@/router";
import { COMMITTEES_BASE_PATH } from "@/pages/people/membership-services/committees/constants";
import {
  PBD_CARD_MANUAL_FULFILLMENT_PATH,
  usePbdCardManualFulfillmentAccess
} from "@/pages/people/membership-services/pbd-card-manual-fulfillment/access";

export const isApplicationsMembershipEnabled = (flags: FlagRecord = {}) => flags["applications-membership"] === true;

export const getMembershipServicesNavLinks = (
  flags: FlagRecord = {},
  {
    canManageApplications = false,
    canViewCommittees = false,
    canQueuePbdCardManualFulfillment = false
  }: {
    canManageApplications?: boolean;
    canViewCommittees?: boolean;
    canQueuePbdCardManualFulfillment?: boolean;
  } = {}
): NavLink<Path>[] => {
  const links: NavLink<Path>[] = [
    ...(canManageApplications
      ? [
          {
            domain: DOMAIN.EVERYONE,
            label: "Applications",
            link: { to: "/people/membership-services/applications" as const },
            icon: "article" as const
          }
        ]
      : []),
    {
      domain: DOMAIN.EVERYONE,
      label: "Achievements",
      link: { to: "/people/membership-services/achievements-report" },
      icon: "emoji_events"
    },
    ...(canManageApplications && isApplicationsMembershipEnabled(flags)
      ? [
          {
            domain: DOMAIN.EVERYONE,
            label: "Member Elections",
            link: { to: "/people/membership-services/member-elections" as const },
            icon: "how_to_vote" as const
          }
        ]
      : [])
  ];

  if (canViewCommittees) {
    links.push({
      domain: DOMAIN.PEOPLE,
      label: "Committees",
      link: {
        to: COMMITTEES_BASE_PATH
      },
      icon: "groups"
    });
  }

  if (canQueuePbdCardManualFulfillment) {
    links.push({
      domain: DOMAIN.EVERYONE,
      label: "PBD Card Manual Fulfillment",
      link: { to: PBD_CARD_MANUAL_FULFILLMENT_PATH },
      icon: "badge"
    });
  }

  return links;
};

export const useMembershipServicesNavLinks = () => {
  const flags = useFlags();
  const canManageApplications = useCanManageApplications();
  const canViewCommittees = useCanViewCommitteesBool();
  const { canQueue } = usePbdCardManualFulfillmentAccess();

  return getMembershipServicesNavLinks(flags, {
    canManageApplications,
    canViewCommittees,
    canQueuePbdCardManualFulfillment: canQueue
  });
};
